import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Flag, CheckCircle, AlertTriangle } from 'lucide-react'
import { databaseService } from '@/services/database'
import { authService } from '@/services/auth'
import { ContentReportReason } from '@/types'
import { useTranslation } from 'react-i18next'

interface ReportContentButtonProps {
  roomId: string
  promptId: string
  imageUrl?: string
  compact?: boolean
}

export default function ReportContentButton({ roomId, promptId, imageUrl }: ReportContentButtonProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [alreadyReported, setAlreadyReported] = useState(false)
  const [error, setError] = useState('')
  const [reason, setReason] = useState<ContentReportReason>('nsfw')
  const [details, setDetails] = useState('')
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null)

  useEffect(() => {
    const check = async () => {
      try {
        const user = await authService.getCurrentUser()
        if (user) {
          const has = await databaseService.hasReported(promptId, user.id)
          setAlreadyReported(has)
        }
      } catch {}
    }
    check()
  }, [promptId])

  // Position the menu in a portal to escape stacking contexts
  useEffect(() => {
    const compute = () => {
      if (!open || !buttonRef.current) return
      const rect = buttonRef.current.getBoundingClientRect()
      const panelWidth = 288 // w-72
      const margin = 8
      const top = rect.bottom + margin
      let left = rect.right - panelWidth
      const maxLeft = window.innerWidth - panelWidth - margin
      if (left > maxLeft) left = maxLeft
      if (left < margin) left = margin
      setCoords({ top, left })
    }

    if (open) compute()

    const onScrollOrResize = () => compute()
    if (open) {
      window.addEventListener('scroll', onScrollOrResize, true)
      window.addEventListener('resize', onScrollOrResize)
    }
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true)
      window.removeEventListener('resize', onScrollOrResize)
    }
  }, [open])

  // Close on outside click or Escape
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!open) return
      const target = e.target as Node
      if (menuRef.current?.contains(target)) return
      if (buttonRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    if (open) {
      document.addEventListener('mousedown', onDocClick)
      document.addEventListener('keydown', onKey)
    }
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const submit = async () => {
    setSubmitting(true)
    setError('')
    try {
      const user = await authService.ensureAuthenticated()
      await databaseService.reportContent({
        roomId,
        promptId,
        reporterId: user.id,
        imageUrl,
        reason,
        details: details?.trim() || undefined,
      })
      setSuccess(true)
      setAlreadyReported(true)
      setOpen(false)
      setDetails('')
    } catch (e: any) {
      setError(e?.message || 'Rapor gönderilemedi')
    } finally {
      setSubmitting(false)
    }
  }

  if (success) {
    return (
      <div className="w-10 h-10 rounded-full flex items-center justify-center bg-green-500/20 text-green-400" title={t('report.success')}>
        <CheckCircle className="w-5 h-5" />
      </div>
    )
  }

  return (
    <div className="relative inline-block">
      <button
        onClick={() => !alreadyReported && setOpen(v => !v)}
        disabled={alreadyReported}
        className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
          alreadyReported 
            ? 'bg-white/10 text-white/40 cursor-not-allowed' 
            : 'bg-white/10 backdrop-blur-md text-white hover:bg-white/20'
        }`}
        aria-haspopup="dialog"
        aria-expanded={open}
        ref={buttonRef}
        title={alreadyReported ? t('report.alreadyReported') : t('report.button')}
      >
        <Flag className="w-5 h-5" />
      </button>

      {open && coords && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          ref={menuRef}
          style={{ top: coords.top, left: coords.left }}
          className="fixed z-[9999] w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-3 safe-area"
        >
          <div className="flex items-center space-x-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            <div className="font-medium text-gray-800 text-sm">{t('report.title')}</div>
          </div>

          <label className="block text-xs text-gray-600 mb-1">{t('report.reason')}</label>
          <select
            className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm mb-2 bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400"
            value={reason}
            onChange={e => setReason(e.target.value as ContentReportReason)}
          >
            <option value="nsfw">{t('report.reasons.nsfw')}</option>
            <option value="violence">{t('report.reasons.violence')}</option>
            <option value="hate">{t('report.reasons.hate')}</option>
            <option value="harassment">{t('report.reasons.harassment')}</option>
            <option value="copyright">{t('report.reasons.copyright')}</option>
            <option value="other">{t('report.reasons.other')}</option>
          </select>

          <label className="block text-xs text-gray-600 mb-1">{t('report.details')}</label>
          <textarea
            className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm resize-none h-20 bg-gray-50 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400"
            placeholder={t('report.detailsPlaceholder') as string}
            value={details}
            onChange={e => setDetails(e.target.value)}
          />

          {error && <div className="text-xs text-red-600 mt-1">{error}</div>}

          <div className="flex items-center justify-end space-x-2 mt-3">
            <button
              onClick={() => setOpen(false)}
              className="text-xs px-3 py-1 rounded-md border border-gray-300 text-gray-800 hover:bg-gray-100"
            >
              {t('report.cancel')}
            </button>
            <button
              onClick={submit}
              disabled={submitting}
              className="text-xs px-3 py-1 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
            >
              {submitting ? t('report.submitting') : t('report.submit')}
            </button>
          </div>
        </div>, document.body
      )}
    </div>
  )
}


